import {
  ActionIcon,
  Box,
  Button,
  Divider,
  Group,
  Loader,
  Popover,
  Stack,
  // eslint-disable-next-line no-restricted-imports
  Text,
  TextInput,
  createStyles,
} from '@mantine/core';
import { IconDotsVertical } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useTheme } from '@emotion/react';
import useTranslation from 'next-translate/useTranslation';
import { showNotification } from '@mantine/notifications';
import { useForm } from '@mantine/form';

import { FEATURE } from '~/common/enums/feature.enum';
import { mailToSupport } from '~/components/utils/mailToSupport';
import { getTimeAgoTranslation } from '~/components/utils/formatters';
import { getPrimaryThemeColor } from '~/config/themeUtils';
import { reportEvent } from '~/domains/analytics';
import { Avatar } from '~/domains/common/components/Avatar/Avatar';
import { useIsFeatureEnabled } from '~/hooks/useIsFeatureEnabled';
import { getComments, addComment } from '~/services/commissioner/commissioner-service.service.api';

type CommentSectionProps = {
  postId: string;
};

const useStyles = createStyles({
  form: { width: '100%' },
});

function AddCommentTextField({ postId }: CommentSectionProps) {
  const { t } = useTranslation('commissioner');
  const { classes } = useStyles();
  const queryClient = useQueryClient();
  const form = useForm({ initialValues: { text: '' } });
  const { mutate: handleAddComment, isLoading: isLoadingComments } = useMutation({
    mutationFn: async () => addComment(postId, form.values.text),
    onSuccess: () => {
      form.reset();
      queryClient.invalidateQueries({
        queryKey: ['commissioner/posts'],
      });

      reportEvent('Commissioner > Comment on Post', {
        post_id: postId,
      });

      form.reset();
    },
    onError: (error: AxiosError<{ message?: string }>) => {
      const errorMessage = error?.response?.data?.message;
      showNotification({
        title: t('hub.posts.comments.commentError'),
        message: errorMessage,
        color: 'red',
        autoClose: 5000,
      });
    },
  });

  return (
    <>
      <Divider mt="sm" mb="lg" />
      <form className={classes.form} onSubmit={form.onSubmit(() => handleAddComment())}>
        <Group noWrap position="apart">
          <TextInput
            w="100%"
            placeholder={t('hub.posts.comments.addComment')}
            {...form.getInputProps('text')}
          />
          <Button type="submit" loading={isLoadingComments}>
            <Text color="black">{t('common.submit')}</Text>
          </Button>
        </Group>
      </form>
    </>
  );
}

function CommentSection({ postId }: CommentSectionProps) {
  const showPostUsernameAndDate = useIsFeatureEnabled(FEATURE.ENABLE_POST_COMMENTS);
  const theme = useTheme();
  const { t } = useTranslation('commissioner');
  // eslint-disable-next-line local-rules/require-query-stale-time
  const { data: commentsData, isLoading } = useQuery({
    queryKey: ['commissioner/posts', `comments/${postId}`],
    queryFn: () => getComments(postId),
  });

  const handleReportComment = (commentId) => {
    const message = `I would like to report a comment. [Post ID: ${postId}; Comment ID: ${commentId}]`;
    mailToSupport({
      subject: 'Report a Comment',
      body: message,
    });
  };

  if (isLoading) {
    return <Loader />;
  }

  if (commentsData.totalCount === 0) {
    return (
      <Box mt="lg">
        <Stack>
          <Text component="span">{t('hub.posts.comments.emptyState')}</Text>
          <AddCommentTextField postId={postId} />
        </Stack>
      </Box>
    );
  }

  return (
    <Box mt="lg">
      {commentsData.data.map((comment) => {
        const { createdAt, username } = showPostUsernameAndDate
          ? comment
          : { username: 'User name', createdAt: '2023-07-21T14:12:41.500Z' };
        const { key, interval } = getTimeAgoTranslation(createdAt);
        return (
          <Group noWrap position="apart" key={comment.id} mb="sm">
            <Group>
              <Avatar radius={50} size={40} />
              <Stack spacing={0}>
                <Group align="baseline" spacing="xs">
                  <Text size={14} fw={700}>
                    {username}
                  </Text>
                  <Text size={10} color="dimmed">
                    {t(key, { count: interval })}
                  </Text>
                </Group>
                <Text w="100%" color="dimmed">
                  {comment.text}
                </Text>
              </Stack>
            </Group>
            <Popover position="bottom-end" trapFocus>
              <Popover.Target>
                <ActionIcon>
                  <IconDotsVertical />
                </ActionIcon>
              </Popover.Target>
              <Popover.Dropdown>
                <Stack>
                  <Button
                    variant="subtle"
                    onClick={() => handleReportComment(comment.id)}
                    styles={{ root: { ':hover': { background: 0 } } }}
                  >
                    <Text color={getPrimaryThemeColor(theme)}>
                      {t('hub.posts.comments.report')}
                    </Text>
                  </Button>
                </Stack>
              </Popover.Dropdown>
            </Popover>
          </Group>
        );
      })}
      <AddCommentTextField postId={postId} />
    </Box>
  );
}

export default CommentSection;
